import { Alert, Button, Card, Space, Typography } from "antd";
import { useEffect, useState } from "react";

import request from "@/api";
import { useUserStore } from "@/views/stores";

interface AdvertisementContent {
    fileId?: number;
}

interface AdvertisementPositionRecord {
    id?: number;
    positionType?: number;
    position?: number;
    displayForm?: number;
    content?: AdvertisementContent[];
    firstContent?: AdvertisementContent[];
    secondContent?: AdvertisementContent[];
}

export interface AdvertisementPageProps {
    loadAdvertisements?: () => Promise<AdvertisementPositionRecord[]>;
    removeAdvertisement?: (id: number) => Promise<unknown>;
    permissions?: string[];
}

const POSITION_TYPES = [
    [1, "首页广告位"],
    [2, "查砂价广告位"],
    [3, "查运价广告位"],
    [4, "货找船广告位"],
    [5, "看资讯广告位"],
    [6, "找货源广告位"],
    [7, "找商机广告位"],
    [8, "资源地图广告位"],
    [9, "找资金广告位"]
] as const;

/** 复用 Vue PC 广告位全量接口。 */
export async function loadAdvertisements(): Promise<AdvertisementPositionRecord[]> {
    const response = await request.service.get<AdvertisementPositionRecord[]>(
        "/dataService/admin/advertisement-position/all"
    );
    return response.data;
}

/** 复用 Vue PC 广告位重置接口。 */
export async function removeAdvertisement(id: number): Promise<unknown> {
    const response = await request.service.delete(`/dataService/admin/advertisement-position/${id}`);
    return response.data;
}

function getUploadedCount(item: AdvertisementPositionRecord): number {
    const content = [...(item.content || []), ...(item.firstContent || []), ...(item.secondContent || [])];
    return content.filter(entry => entry.fileId).length;
}

/** 保持 Vue 根据广告位类型选择两种编辑表单的路由规则。 */
function getEditHref(item: AdvertisementPositionRecord): string {
    const editRoute =
        item.positionType === 1 || (item.positionType === 6 && item.position === 2) ? "twoEditType" : "adEdit";
    return `/userOperation/adBanner/${editRoute}?id=${item.id ?? ""}&positionType=${item.positionType ?? ""}`;
}

/** PC 端广告位首屏，按 Vue positionType 分组并按 position 排序。 */
export function AdvertisementPage({
    loadAdvertisements: load = loadAdvertisements,
    removeAdvertisement: remove = removeAdvertisement,
    permissions
}: AdvertisementPageProps) {
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const banButton = useUserStore(state => state.banButton);
    // 对齐 Vue listItemImg 的 v-auth：无广告写权限时隐藏修改、重置入口。
    const canManage = (permissions ?? storePermissions).includes("ROLE_SS_AD_W");
    const [rows, setRows] = useState<AdvertisementPositionRecord[]>([]);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [reloadToken, setReloadToken] = useState(0);

    useEffect(() => {
        let disposed = false;
        void load()
            .then(result => {
                if (!disposed) setRows(result || []);
            })
            .catch(() => {
                if (!disposed) setErrorMessage("广告位加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [load, reloadToken]);

    const resetAdvertisement = async (id?: number) => {
        if (id == null) return;
        // Vue confirmable 在重置前要求确认，取消时不应触发删除请求。
        if (!window.confirm("是否重置，重置后用户PC端将展示系统默认图片")) return;
        try {
            await remove(id);
            setReloadToken(value => value + 1);
        } catch {
            setErrorMessage("广告位重置失败");
        }
    };

    return (
        <section aria-label="advertisement-page" className="flex h-full min-h-full flex-col">
            <Card className="flex h-full flex-1 flex-col">
                <Typography.Title className="mb-1 mt-0" level={4}>
                    PC端-首页广告位
                </Typography.Title>
                <Typography.Text className="mb-4">请选择你想投放的首页广告位置</Typography.Text>
                {errorMessage ? <Alert className="mt-4" message={errorMessage} role="alert" type="error" /> : null}
                <Space className="mt-4" direction="vertical" size="large">
                    {POSITION_TYPES.map(([code, title]) => {
                        const items = rows
                            .filter(item => item.positionType === code)
                            .sort((left, right) => (left.position || 0) - (right.position || 0));
                        return (
                            <section key={code} aria-label={title}>
                                <Typography.Title level={5}>{title}</Typography.Title>
                                <div className="grid grid-cols-3 gap-4">
                                    {items.length ? (
                                        items.map(item => (
                                            <Card key={item.id ?? `${code}-${item.position}`} size="small">
                                                <Typography.Text>{item.position}号广告位</Typography.Text>
                                                <div>
                                                    {item.displayForm === 3
                                                        ? "不展示"
                                                        : `已上传${getUploadedCount(item)}张`}
                                                </div>
                                                {canManage ? (
                                                    <>
                                                        <a
                                                            aria-label={`修改广告位 ${item.position}`}
                                                            aria-disabled={banButton || undefined}
                                                            href={getEditHref(item)}
                                                            onClick={event => {
                                                                // 链接没有原生 disabled，封禁态需阻止 Vue 修改入口继续跳转。
                                                                if (banButton) event.preventDefault();
                                                            }}
                                                        >
                                                            修改
                                                        </a>
                                                        <Button
                                                            aria-label={`重置广告位 ${item.position}`}
                                                            disabled={banButton}
                                                            type="link"
                                                            onClick={() => void resetAdvertisement(item.id)}
                                                        >
                                                            重置
                                                        </Button>
                                                    </>
                                                ) : null}
                                            </Card>
                                        ))
                                    ) : (
                                        <Typography.Text type="secondary">暂无广告位</Typography.Text>
                                    )}
                                </div>
                            </section>
                        );
                    })}
                </Space>
            </Card>
        </section>
    );
}

export default AdvertisementPage;
